import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/api';

const SedeContext = createContext();

export const useSede = () => {
  const context = useContext(SedeContext);
  if (!context) {
    throw new Error('useSede must be used within a SedeProvider');
  }
  return context;
};

export const SedeProvider = ({ children, user }) => {
  const [selectedSedes, setSelectedSedes] = useState([]);
  const [availableSedes, setAvailableSedes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load available sedes on mount
  useEffect(() => {
    fetchAvailableSedes();
  }, []);

  // Auto-select user's sede for non-admin users
  useEffect(() => {
    if (user && !user.groups?.includes('admin') && user.sede && availableSedes.length > 0) {
      const userSede = availableSedes.find(sede => sede.id === user.sede.id);
      if (userSede && selectedSedes.length === 0) {
        console.log('🔒 Auto-selecting user sede for non-admin:', userSede);
        setSelectedSedes([userSede]);
        localStorage.setItem('selectedSedes', JSON.stringify([userSede]));
      }
    }
  }, [user, availableSedes, selectedSedes.length]);

  const fetchAvailableSedes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/studio/sedes/');
      // Filtrar solo las sedes activas
      const activeSedes = response.data.filter(sede => sede.status === true);
      setAvailableSedes(activeSedes);
    } catch (err) {
      console.error('Error fetching sedes:', err);
      setError('Error al cargar las sedes');
    } finally {
      setLoading(false);
    }
  };

  const updateSelectedSedes = (newSedes) => {
    setSelectedSedes(newSedes);
    // Store in localStorage for persistence
    localStorage.setItem('selectedSedes', JSON.stringify(newSedes));
    
    // Dispatch custom event to notify components of sede change
    // Use setTimeout to ensure state is updated before dispatching event
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('sedeChanged', { 
        detail: { selectedSedes: newSedes } 
      }));
    }, 100);
  };

  const clearSelectedSedes = () => {
    setSelectedSedes([]);
    localStorage.removeItem('selectedSedes');
  };

  const selectAllSedes = () => {
    const newSedes = [...availableSedes];
    setSelectedSedes(newSedes);
    localStorage.setItem('selectedSedes', JSON.stringify(newSedes));
    // Dispatch custom event to notify components of sede change
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('sedeChanged', { 
        detail: { selectedSedes: newSedes } 
      }));
    }, 100);
  };

  const selectGlobal = () => {
    setSelectedSedes([]);
    localStorage.removeItem('selectedSedes');
    // Dispatch custom event to notify components of sede change
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('sedeChanged', { 
        detail: { selectedSedes: [] } 
      }));
    }, 100);
  };

  // Load selected sedes from localStorage on mount
  useEffect(() => {
    const savedSedes = localStorage.getItem('selectedSedes');
    if (savedSedes) {
      try {
        const parsedSedes = JSON.parse(savedSedes);
        setSelectedSedes(parsedSedes);
      } catch (err) {
        console.error('Error parsing saved sedes:', err);
        localStorage.removeItem('selectedSedes');
      }
    }
  }, []);

  // Get sede IDs for API calls
  const getSedeIds = () => {
    return selectedSedes.map(sede => sede.id);
  };

  // Get sede IDs as comma-separated string for query parameters
  const getSedeIdsString = () => {
    return getSedeIds().join(',');
  };

  // Check if a specific sede is selected
  const isSedeSelected = (sedeId) => {
    return selectedSedes.some(sede => sede.id === sedeId);
  };

  // Check if global mode (no sede filtering)
  const isGlobalMode = () => {
    return selectedSedes.length === 0;
  };

  // Get display text for current selection
  const getSelectionText = () => {
    if (isGlobalMode()) {
      return '🌐 Global (Todas las sedes)';
    } else if (selectedSedes.length === 1) {
      return `📍 ${selectedSedes[0].name}`;
    } else {
      return `📍 ${selectedSedes.length} sedes seleccionadas`;
    }
  };

  // Add sede to API request headers
  const addSedeHeaders = (existingHeaders = {}) => {
    const sedeIds = getSedeIdsString();
    if (sedeIds && sedeIds.length > 0) {
      return {
        ...existingHeaders,
        'X-Sedes-Selected': sedeIds
      };
    }
    return existingHeaders;
  };

  // Add sede to query parameters
  const addSedeParams = (params = {}) => {
    const sedeIds = getSedeIdsString();
    if (sedeIds && sedeIds.length > 0) {
      params.sede_ids = sedeIds;
    }
    return params;
  };

  // Get single sede ID (for backward compatibility)
  const getSedeId = () => {
    if (selectedSedes.length === 1) {
      return selectedSedes[0].id;
    }
    return null;
  };

  const value = {
    selectedSedes,
    availableSedes,
    loading,
    error,
    updateSelectedSedes,
    clearSelectedSedes,
    selectAllSedes,
    selectGlobal,
    getSedeIds,
    getSedeIdsString,
    getSedeId,
    isSedeSelected,
    isGlobalMode,
    getSelectionText,
    addSedeHeaders,
    addSedeParams,
    fetchAvailableSedes
  };

  return (
    <SedeContext.Provider value={value}>
      {children}
    </SedeContext.Provider>
  );
};
